import { useEffect, useRef } from 'react';
import { closeHistory } from '@tiptap/pm/history';
import { NodeSelection } from '@tiptap/pm/state';
import { ChevronLeft, ChevronRight, MousePointer2, PanelRightClose, X } from 'lucide-react';
import { useActiveEditor } from '@/store/editorContext';
import { useInvestmentInspectorStore } from '@/store/investmentInspectorStore';
import { useBlockStore } from '@/store/blockStore';
import { getBlockTitle } from './PageListSidebar';
import {
  confirmProductRemoval,
  duplicateInvestmentProduct,
  findInvestmentProduct,
  updateInvestmentProduct,
} from '@/editor/investmentProduct';
import {
  investmentDestinations,
  investmentGroupForPage,
  investmentPhaseRows,
  selectWorkspacePosition,
} from '@/editor/quoteWorkspace';
import InvestmentPageOverview from './InvestmentPageOverview';
import ProductPricingPopover from './ProductPricingPopover';
import QuoteCustomerOverview, { type QuoteCustomerOverviewProps } from './QuoteCustomerOverview';
import TeamPageEditor from './TeamPageEditor';
import { BlockType } from '@/types';

export default function SideBySideInspector({
  selectedBlockId,
  onDocumentMode,
  onCollapse,
  customerContext,
}: {
  selectedBlockId: string | null;
  onDocumentMode: () => void;
  onCollapse: () => void;
  customerContext: QuoteCustomerOverviewProps;
}) {
  const { documentEditor: editor } = useActiveEditor();
  const group = useInvestmentInspectorStore((state) => state.group);
  const phase = useInvestmentInspectorStore((state) => state.phase);
  const scroller = useRef<HTMLDivElement>(null);
  const selectedPage = useBlockStore((state) => state.blocks.find((block) => block.id === selectedBlockId));
  const product = phase?.row?.investmentProduct;
  const destination =
    editor && phase
      ? investmentDestinations(editor.state.doc).find((entry) => entry.tableId === phase.tableId)
      : null;
  const positions =
    editor && destination
      ? investmentPhaseRows(editor.state.doc, destination).filter((row) => row.product)
      : [];
  const productIndex = positions.findIndex((row) => row.product?.id === product?.id);
  const investmentPage = editor && investmentGroupForPage(editor.state.doc, selectedBlockId);
  useEffect(() => {
    scroller.current?.scrollTo({ top: 0 });
  }, [selectedBlockId, phase?.tableId, product?.id]);

  const navigateToParent = (level: 'investment' | 'phase') => {
    if (!editor || !phase) return;
    const pos =
      level === 'investment'
        ? investmentGroupForPage(editor.state.doc, phase.groupId)?.pos
        : investmentDestinations(editor.state.doc).find(
            (entry) => entry.tableId === phase.tableId
          )?.headerPos;
    if (pos === undefined) return;
    editor.view.dom.dataset.workspaceSelection = 'active';
    editor.view.dispatch(
      editor.state.tr.setSelection(NodeSelection.create(editor.state.doc, pos))
    );
    const target = editor.view.nodeDOM(pos);
    if (target instanceof HTMLElement) {
      (level === 'investment' ? target.querySelector('.investment-main-header') : target)
        ?.scrollIntoView({ block: 'nearest' });
    }
  };
  const breadcrumb = phase && (
    <nav className="workspace-breadcrumb" aria-label="Breadcrumb">
      <ol>
        <li>
          <button type="button" onClick={() => navigateToParent('investment')}>
            Investering
          </button>
        </li>
        <li>
          <span aria-hidden="true">/</span>
          {product ? (
            <button type="button" onClick={() => navigateToParent('phase')}>
              {phase.title}
            </button>
          ) : (
            <span aria-current="page">{phase.title}</span>
          )}
        </li>
        {product && (
          <li>
            <span aria-hidden="true">/</span>
            <span aria-current="page">
              {`Product ${productIndex + 1} van ${positions.length}`}
            </span>
          </li>
        )}
      </ol>
    </nav>
  );

  const clearSelection = () => {
    if (editor) selectWorkspacePosition(editor, 0);
    useInvestmentInspectorStore.setState({ group: null, phase: null });
  };
  const navigateProduct = (direction: number) => {
    if (!editor || !phase || !product) return;
    const next = positions[productIndex + direction];
    if (next) {
      selectWorkspacePosition(editor, next.pos);
      editor.view.dom
        .querySelector(`[data-product-group="${next.product!.id}"]`)
        ?.scrollIntoView({ block: 'nearest' });
    }
  };

  return (
    <section
      className="workspace-inspector"
      data-investment-inspector
      aria-label="Offerte bewerken"
    >
      <header className="workspace-panel-heading">
        <h2>{selectedPage || phase || group ? 'Bewerken' : 'Offertegegevens'}</h2>
        <span className="workspace-follow-selection">
          <MousePointer2 size={14} /> Selectie volgen
        </span>
        {(selectedPage || phase || group) && (
          <button
            type="button"
            className="workspace-icon-button"
            onClick={() => {
              clearSelection();
              onCollapse();
            }}
            aria-label="Selectie wissen en sluiten"
            title="Selectie wissen en sluiten"
          >
            <X size={16} />
          </button>
        )}
        <button
          type="button"
          className="workspace-icon-button workspace-pane-collapse"
          onClick={onCollapse}
          aria-label="Bewerkpaneel inklappen"
          title="Bewerkpaneel inklappen"
        >
          <PanelRightClose size={16} />
        </button>
      </header>
      <div
        className={
          product ? 'workspace-inspector-body' : 'workspace-inspector-scroll scrollbar-thin'
        }
        ref={scroller}
      >
        {product && phase && editor ? (
          <>
            <div className="workspace-product-context">
              {breadcrumb}
              <div>
                <button
                  type="button"
                  className="workspace-icon-button"
                  aria-label="Vorig product"
                  disabled={productIndex <= 0}
                  onClick={() => navigateProduct(-1)}
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  type="button"
                  className="workspace-icon-button"
                  aria-label="Volgend product"
                  disabled={productIndex < 0 || productIndex >= positions.length - 1}
                  onClick={() => navigateProduct(1)}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
            <ProductPricingPopover
              key={product.id}
              product={product}
              currency={group?.currency ?? 'EUR'}
              layout="panel"
              onChange={(next, separateHistory) =>
                updateInvestmentProduct(editor, next, separateHistory)
              }
              onClose={clearSelection}
              onDuplicate={() => duplicateInvestmentProduct(editor, product.id)}
              onRemove={async () => {
                if (!(await confirmProductRemoval(product))) return;
                const target = findInvestmentProduct(editor, product.id);
                if (target) {
                  editor.view.dispatch(
                    closeHistory(editor.state.tr).delete(
                      target.pos,
                      target.pos + target.node.nodeSize
                    )
                  );
                  clearSelection();
                }
              }}
            />
          </>
        ) : editor && (phase || investmentPage) && selectedBlockId ? (
          <>
            {phase && <div className="workspace-product-context">{breadcrumb}</div>}
            <InvestmentPageOverview editor={editor} blockId={phase?.groupId ?? selectedBlockId}
              activePhase={phase} onDocumentMode={onDocumentMode} />
          </>
        ) : editor && selectedPage?.type === BlockType.TEAM ? (
          <TeamPageEditor key={selectedPage.id} editor={editor} blockId={selectedPage.id} />
        ) : selectedPage ? (
          <div className="workspace-empty">
            <MousePointer2 size={28} />
            <h3>{getBlockTitle(selectedPage)}</h3>
            <p>
              Bewerk deze pagina direct links. Tekst, opmaak, paginastijl, afbeeldingen en
              pagina-instellingen werken zoals in de documentweergave.
            </p>
            <p>Versleep de pagina’s in de sidebar of gebruik de pijltjes om ze te ordenen.</p>
          </div>
        ) : (
          <QuoteCustomerOverview {...customerContext} />
        )}
      </div>
      <footer className="workspace-inspector-footer">
        {product || selectedPage?.type === BlockType.TEAM
          ? 'Wijzigingen worden automatisch opgeslagen'
          : 'Het bewerkpaneel volgt je selectie'}
      </footer>
    </section>
  );
}
